import React, { FC, useContext, useEffect, useState } from "react";
import { Button, Form, Input, Row, Select, Space, Tooltip, } from "antd";
import { NumericInput } from "../../../core/component";
import { CloseOutlined, PlusOutlined, WarningOutlined, } from "@ant-design/icons";
import { translate } from "../../../core";
import { resolveNumber } from "../../../core/until";
import styles from "./WeddingRegistry.module.scss";
import { Item } from "../../inventory/type";
import { FormEntryField, PageContextValue } from "../../../core/type";
import { WeddingRegistry, WeddingRegistryEntry } from "../type";
import { WeddingRegistryPageContext } from "./WeddingRegistryPageContext";
import { mapEntryLine } from "../model-mapper/wedding-registry-entity-to-entry";
import { getOrderPrice } from "../../order/method/order-pricing";
import { useQuery } from "@apollo/client";
import { GetItemsForLookup } from "../../inventory/graphql/item.graphql";
import Enumerable from "linq";
import { InputCard } from "../../inventory/component/InputCard";
import { NamePath } from "antd/es/form/interface";

type WeddingRegistryLinesProps = {
  totalPrice: number;
  onAmountsChanged: () => void;
  showOnWeb: boolean;
};

export const WeddingRegistryLines = ({
  onAmountsChanged,
  totalPrice,
  showOnWeb,
}: WeddingRegistryLinesProps) => {
  const [forceUpdate, setForceUpdate] = useState<boolean>(false);
  const { form } = useContext<
    PageContextValue<WeddingRegistry, WeddingRegistryEntry>
  >(WeddingRegistryPageContext);

  const onSelectItem = (index: number) => (item: Item) => {
    const fieldPath: NamePath<WeddingRegistryEntry> = ["lines", index];
    form?.setFieldValue([...fieldPath, "description"], item.description);
    form?.setFieldValue(
      [...fieldPath, "unitPrice"],
      getOrderPrice(item)?.price ?? 0
    );
    onLineChanged(index);
  };

  const onLineChanged = (index: number) => {
    const fieldPath: NamePath<WeddingRegistryEntry> = ["lines", index];
    const entry = form?.getFieldValue(fieldPath);
    const quantity = resolveNumber(entry.quantity);
    const quantityTaken = resolveNumber(entry.quantityTaken);
    const quantityAvailable = quantity - quantityTaken;
    const line = form?.getFieldValue(fieldPath);

    mapEntryLine(line);

    form?.setFieldValue(fieldPath, line);

    form?.setFieldValue(
      [...fieldPath, "quantityAvailable"],
      quantityAvailable >= 0 ? quantityAvailable : 0
    );
    onAmountsChanged();
  };

  useEffect(() => {
    setForceUpdate(false);
  }, [forceUpdate]);

  return (
    <InputCard contained>
      <Form.List name="lines">
        {(fields, { add, remove }) => (
          <>
            <Row
              align="middle"
              justify="space-between"
              style={{ width: "70%" }}
              className={styles["amountsWrapper"]}
            >
              <Form.Item
                style={{
                  marginBottom: "0",
                  display: "flex",
                  flexDirection: "row",
                  gap: "1rem",
                }}
              >
                <Button
                  className="btn-primary-gradient"
                  onClick={() => add({ quantity: 1 })}
                  icon={<PlusOutlined />}
                >
                  {translate("add", "items")}
                </Button>
              </Form.Item>

              <Form.Item label={translate("total_price")}>
                <NumericInput
                  value={totalPrice}
                  className={`${styles.readOnlyInput} ${styles.amountInput} w-medium`}
                  disabled
                />
              </Form.Item>
            </Row>
            <table className="table f-medium bg-white">
              <thead>
                <tr>
                  <th className="text-center" style={{ width: "15%" }}>
                    {translate("item_number_abbr")}
                  </th>
                  <th className="text-center" style={{ width: "15%" }}>
                    {translate("quantity_desired")}
                  </th>
                  <th className="text-center" style={{ width: "30%" }}>
                    {translate("description")}
                  </th>
                  <th className="text-center" style={{ width: "10%" }}>
                    {translate("unit_price")}
                  </th>
                  <th className="text-center" style={{ width: "10%" }}>
                    {translate("extended")}
                  </th>
                  <th className="text-center" style={{ width: "15%" }}>
                    {translate("quantity_available")}
                  </th>
                  <th className="text-center" style={{ width: "3%" }}></th>
                </tr>
              </thead>

              <tbody className={styles["orderLines"]}>
                {fields.map((field, index) => (
                  <tr key={`${field.name}-${field.key}`}>
                    <td>
                      <Form.Item
                        name={[field.name, "item", "id"]}
                        rules={[
                          {
                            required: true,
                            message: translate("missing_field_message", "item"),
                          },
                        ]}
                        className={styles["orderLineItemNumber"]}
                      >
                        <ItemCombo
                          showOnWeb={showOnWeb}
                          onSelect={onSelectItem(index)}
                        />
                      </Form.Item>
                    </td>

                    <td>
                      <Form.Item
                        rules={[
                          {
                            required: true,
                            message: translate(
                              "missing_field_message",
                              "quantity_abbr"
                            ),
                          },
                        ]}
                        name={[field.name, "quantity"]}
                      >
                        <NumericInput
                          className={`text-center`}
                          onChange={() => onLineChanged(index)}
                        />
                      </Form.Item>
                    </td>

                    <td>
                      <Form.Item
                        noStyle
                        name={[field.name, "description"]}
                        rules={[
                          {
                            required: true,
                            message: translate(
                              "missing_field_message",
                              "description"
                            ),
                          },
                        ]}
                      >
                        <Input />
                      </Form.Item>
                    </td>

                    <td>
                      <Form.Item
                        name={[field.name, "unitPrice"]}
                        rules={[
                          {
                            required: true,
                            message: translate(
                              "missing_field_message",
                              "unit_price"
                            ),
                          },
                        ]}
                      >
                        <NumericInput
                          className={styles.amountInput}
                          onChange={() => onLineChanged(index)}
                        />
                      </Form.Item>
                    </td>

                    <td>
                      <Form.Item name={[field.name, "extendedPrice"]}>
                        <NumericInput
                          className={`${styles.readOnlyInput} ${styles.amountInput}`}
                          disabled={true}
                        />
                      </Form.Item>
                    </td>
                    <td>
                      <Form.Item name={[field.name, "quantityAvailable"]}>
                        <NumericInput
                          className={`${styles.readOnlyInput} ${styles.amountInput}`}
                          disabled={true}
                          decimalPlaces={0}
                        />
                      </Form.Item>
                    </td>

                    <td>
                      <Tooltip title={translate("remove")}>
                        <Button
                          onClick={() => {
                            remove(index);
                            setTimeout(() => onAmountsChanged(), 100);
                          }}
                          type="text"
                          shape="circle"
                          icon={<CloseOutlined />}
                        />
                      </Tooltip>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </Form.List>
    </InputCard>
  );
};

const ItemCombo: FC<
  FormEntryField<number> & {
    showOnWeb: boolean;
    onSelect: (item: Item) => void;
  }
> = ({ onChange, onSelect, value, showOnWeb }) => {
  const [search, setSearch] = useState<string>();
  const { data, loading } = useQuery(GetItemsForLookup, {
    variables: {
      request: {
        showOnWeb: showOnWeb ? true : undefined,
        search,
        take: 20,
        skip: 0,
      },
    },
  });

  const { data: selectedItem, loading: selectedItemLoading } = useQuery(
    GetItemsForLookup,
    {
      variables: {
        request: {
          showOnWeb: showOnWeb ? true : undefined,
          take: 20,
          skip: 0,
          id: value ?? 0,
        },
      },
      skip: !value,
    }
  );

  return (
    <Select
      value={value}
      loading={loading || selectedItemLoading}
      options={Enumerable.from(data?.itemsFind.data ?? [])
        .concat(
          selectedItem?.itemsFind.data ??
            (value
              ? ([
                  {
                    id: value,
                    sku: (
                      <Space className="text-warning">
                        <WarningOutlined />
                        No Web
                      </Space>
                    ) as any,
                  },
                ] as Item[])
              : [])
        )
        .distinct((it) => it.id)
        .select((it) => ({
          label: it.sku,
          value: it.id,
          item: it,
        }))
        .orderBy((it) => it.label)
        .toArray()}
      filterOption={false}
      showSearch
      onSearch={setSearch}
      onChange={onChange}
      onSelect={(_, option) => onSelect(option.item)}
    />
  );
};
